import { useRef, useState } from "react";
import { useHotelsStyles, useModuleBEM } from "@b2bportal/core-themes";
import {
  CoreHotelsComponent,
  type PlatformComponentProps,
} from "@b2bportal/core-types";
import type {
  TripAdvisorComment,
  TripAdvisorReviews,
} from "@b2bportal/lodging-api";
import { Dialog, DialogContent, DialogTrigger } from "@tenant/ui";
import { CustomerReviews } from "../CustomerReview";
import { TripAdvisorRatingOneRow } from "../TripAdvisorRating/variants";
import defaultStyles from "./CustomerReviewsSection.module.scss";
import { useCustomerReviewsSection } from "./hooks";

export interface CustomerReviewsSectionComponentProps
  extends PlatformComponentProps {
  reviews: TripAdvisorReviews;
}

const COMPONENT_KEY = CoreHotelsComponent.CustomerReviewsSection;

export const CustomerReviewsSection = (
  props: CustomerReviewsSectionComponentProps
) => {
  const styles = useHotelsStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const { context, text } = useCustomerReviewsSection(props);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedReviewId, setSelectedReviewId] = useState<string | null>(null);
  const reviewRefsMap = useRef<Map<string, HTMLLIElement>>(new Map());
  const reviewListContainerRef = useRef<HTMLUListElement | null>(null);

  // Callback when review list container mounts - this means dialog content is rendered
  const onReviewListMount = (el: HTMLUListElement | null) => {
    reviewListContainerRef.current = el;

    if (el && selectedReviewId) {
      // Dialog content is now rendered, scroll to the selected review
      const reviewElement = reviewRefsMap.current.get(selectedReviewId);
      if (reviewElement) {
        reviewElement.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  };

  const handleSeeFullReview = (reviewId: string) => {
    setSelectedReviewId(reviewId);
    setIsDialogOpen(true);
  };

  const handleDialogOpenChange = (open: boolean) => {
    setIsDialogOpen(open);
    if (!open) {
      // Clear selected review when dialog closes
      setSelectedReviewId(null);
    }
  };

  if (context.reviewsToDisplay.length === 0) {
    return null;
  }

  const generateReviewId = (review: TripAdvisorComment) => {
    return `${review.author}-${review.title}`;
  };

  return (
    <div className={block}>
      <h2 className={cn("title")}>{text.title}</h2>
      <TripAdvisorRatingOneRow
        tripAdvisorReviews={context.reviews}
        className={cn("summary")}
      />
      <div className={cn("review-list-container")}>
        <div className={cn("review-list")}>
          {context.reviewsToDisplay.map((review, index) => {
            const reviewId = generateReviewId(review);
            return (
              <div className={cn("review-card")} key={reviewId}>
                <CustomerReviews
                  review={review}
                  showSeeFullReview
                  onSeeFullReview={() => handleSeeFullReview(reviewId)}
                />
              </div>
            );
          })}
        </div>
      </div>

      <Dialog open={isDialogOpen} onOpenChange={handleDialogOpenChange}>
        <DialogTrigger className={cn("see-all-reviews")}>
          {text.seeAllReviews}
        </DialogTrigger>
        <DialogContent title={text.title}>
          <TripAdvisorRatingOneRow
            tripAdvisorReviews={context.reviews}
            className={cn("summary")}
          />
          {context.reviews.categories.length > 0 ? (
            <div className={cn("category-grid")}>
              {context.reviews.categories.map((category) => {
                const percentage = category.score * 100;
                const score = category.score * 10;
                const displayScore = score.toFixed(1);
                const isLowScore = score < 7;
                return (
                  <div
                    className={cn("category-item")}
                    key={`${category.categoryName}`}
                    data-low-score={isLowScore}
                  >
                    <div className={cn("category-header")}>
                      <span className={cn("category-name")}>
                        {category.categoryName}
                      </span>
                      <span className={cn("category-score")}>
                        {displayScore}
                      </span>
                    </div>
                    <div className={cn("category-bar-container")}>
                      <div
                        className={cn("category-bar")}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : null}
          <ul
            className={cn("review-list", ["vertical"])}
            ref={onReviewListMount}
          >
            {context.comments.map((review) => {
              const reviewId = generateReviewId(review);
              return (
                <li
                  className={cn("review-item")}
                  key={reviewId}
                  ref={(el) => {
                    if (el) {
                      reviewRefsMap.current.set(reviewId, el);
                    } else {
                      reviewRefsMap.current.delete(reviewId);
                    }
                  }}
                >
                  <CustomerReviews
                    review={review}
                    showReviewInfo
                    showTripAdvisorRating
                  />
                </li>
              );
            })}
          </ul>
        </DialogContent>
      </Dialog>
    </div>
  );
};
